import { supabase } from '../utils/supabase/client';

class ProductServiceAPI {
  constructor() {
    // Detectar se está rodando no Vercel ou localmente para upload de imagem
    this.isVercel = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || 
       window.location.hostname.includes('vercel.app') || 
       window.location.hostname.includes('vercel.com') ||
       window.location.hostname.includes('gpreto.space') ||
       window.location.hostname !== 'localhost');
    
    this.apiUrl = process.env.REACT_APP_API_URL
      || (this.isVercel ? '/api' : 'http://localhost:3001/api');
  }

  // Detectar se é mobile
  isMobile() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  async createProduct(productData) {
    try {
      const PRODUCT_COLUMNS = ['factoryId', 'name', 'ref', 'imageUrl', 'uPrice', 'unit', 'unitCtn', 'cbm', 'unitWeight', 'gW', 'l', 'w', 'h', 'remark', 'audioUrl', 'audioUrls', 'productDimensions', 'galleryImages'];
      const cleanData = Object.fromEntries(
        Object.entries(productData).filter(([key, value]) =>
          PRODUCT_COLUMNS.includes(key) &&
          value !== undefined && value !== null && value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        )
      );
      
      
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from('products')
        .insert([{ ...cleanData, createdAt: timestamp, updatedAt: timestamp }])
        .select('id');

      if (error) {
        throw error;
      }

      return { id: data?.[0]?.id, ...cleanData };
    } catch (error) {
      throw error;
    }
  }

  async getAllProducts() {
    try {

      const { data: products, error } = await supabase
        .from('products')
        .select('*')
        .order('createdAt', { ascending: false })
        .limit(100);

      if (error) {
        throw error;
      }

      const uniqueFactoryIds = Array.from(
        new Set((products || []).map(product => product.factoryId).filter(Boolean))
      );

      let factoryMap = {};
      if (uniqueFactoryIds.length > 0) {
        const { data: factories, error: factoryError } = await supabase
          .from('factories')
          .select('*')
          .in('id', uniqueFactoryIds);

        if (factoryError) {
          throw factoryError;
        }

        factoryMap = (factories || []).reduce((acc, factory) => {
          acc[factory.id] = factory;
          return acc;
        }, {});
      }

      const productsWithFactory = (products || []).map(product => ({
        ...product,
        factory: product.factoryId ? factoryMap[product.factoryId] || null : null
      }));

      const result = productsWithFactory.map(product => {
        if (product.price && typeof product.price === 'string') {
          return { ...product, price: parseFloat(product.price) };
        }
        return product;
      });

      return result;
    } catch (error) {
      throw error;
    }
  }

  async updateProduct(id, productData) {
    try {
      const PRODUCT_COLUMNS = ['factoryId', 'name', 'ref', 'imageUrl', 'uPrice', 'unit', 'unitCtn', 'cbm', 'unitWeight', 'gW', 'l', 'w', 'h', 'remark', 'audioUrl', 'audioUrls', 'productDimensions', 'galleryImages'];
      const cleanData = Object.fromEntries(
        Object.entries(productData)
          .filter(([key]) => PRODUCT_COLUMNS.includes(key))
          .map(([key, value]) => [key, value === '' ? null : value])
          .filter(([, value]) => value !== undefined)
      );

      const timestamp = new Date().toISOString();
      const { error } = await supabase
        .from('products')
        .update({ ...cleanData, updatedAt: timestamp })
        .eq('id', id);

      if (error) {
        console.error('Supabase updateProduct error:', JSON.stringify(error));
        throw error;
      }

      return { id, ...cleanData };
    } catch (error) {
      throw error;
    }
  }

  async deleteProduct(id) {
    try {
      
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  async getProductById(id) {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async uploadProductImage(file) {
    try {
      if (!file) {
        throw new Error('Nenhum arquivo fornecido');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Apenas arquivos de imagem são permitidos');
      }

      if (file.size > 25 * 1024 * 1024) {
        throw new Error('Arquivo muito grande. Máximo 25MB permitido.');
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${this.apiUrl}/upload-image`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro no upload');
      }

      const result = await response.json();
      
      if (this.isVercel) {
        return result.imageUrl;
      } else {
        const filename = result.imageUrl.split('/').pop();
        return `${this.apiUrl}/image/${filename}`;
      }
    } catch (error) {
      throw new Error(`Erro no upload: ${error.message}`);
    }
  }

  async getFactoryById(factoryId) {
    try {
      const { data, error } = await supabase
        .from('factories')
        .select('*')
        .eq('id', factoryId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async testConnection() {
    try {
      const { error } = await supabase
        .from('products')
        .select('id')
        .limit(1);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      return false;
    }
  }
}

const productServiceAPI = new ProductServiceAPI();
export default productServiceAPI;
